"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.listGenerationLogQuerySchema = exports.resumeSubscriptionSchema = exports.listSubscriptionsQuerySchema = exports.updateSubscriptionSchema = exports.createSubscriptionSchema = exports.subscriptionItemInputSchema = void 0;
exports.checkSubscriptionDates = checkSubscriptionDates;
const zod_1 = require("zod");
const constants_1 = require("../constants");
const enums_1 = require("../enums");
const common_schema_1 = require("./common.schema");
const SUB_FREQUENCY_VALUES = Object.values(enums_1.SubFrequency);
const SUB_STATUS_VALUES = Object.values(enums_1.SubStatus);
const VAT_CATEGORY_VALUES = Object.values(enums_1.VatCategory);
/** `subscription_generation_log.status` is a plain TEXT column (task-2's
 * schema doesn't back it with a Postgres enum — see schema.prisma), so
 * these two values live here rather than in `enums.ts`. */
const GENERATION_LOG_STATUS_VALUES = ['SUCCESS', 'FAILED'];
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
/** A syntactically well-formed "YYYY-MM-DD" that is also a real calendar
 * date ("2026-02-30" is not) — same check as invoice.schema.ts's
 * `isoDateField`, kept as a local copy (each schema file in this package
 * owns its own date validator; see vat.schema.ts/invoice.schema.ts). */
function isRealIsoDate(value) {
    const parsed = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}
const isoDateField = zod_1.z
    .string()
    .trim()
    .regex(ISO_DATE_REGEX, 'Data trebuie să fie în formatul YYYY-MM-DD.')
    .refine(isRealIsoDate, 'Dată invalidă.');
const ZERO_REGEX = /^0+(\.0+)?$/;
/** Quantity on a subscription item: unlike an invoice draft line, there is
 * no storno-style "negatives are legitimate elsewhere" carve-out to
 * preserve — a recurring line is always a positive quantity charged every
 * period, so the regex itself simply doesn't accept a leading '-'. */
const quantityField = zod_1.z
    .string()
    .trim()
    .regex(/^\d+(\.\d{1,3})?$/, 'Cantitatea trebuie să fie un număr pozitiv cu cel mult 3 zecimale.')
    .refine((value) => !ZERO_REGEX.test(value), 'Cantitatea nu poate fi zero.');
exports.subscriptionItemInputSchema = zod_1.z.object({
    productId: zod_1.z.uuid('productId invalid.').optional(),
    description: zod_1.z.string().trim().min(1, 'Descrierea este obligatorie.'),
    quantity: quantityField,
    unitCode: zod_1.z.enum(constants_1.UNIT_CODE_VALUES, 'Unitate de măsură invalidă.'),
    unitLabel: zod_1.z.string().trim().min(1, 'Unitatea de măsură este obligatorie.'),
    unitPrice: zod_1.z
        .string()
        .trim()
        .regex(/^\d+(\.\d{1,2})?$/, 'Prețul trebuie să fie un număr pozitiv cu cel mult 2 zecimale.'),
    vatCategory: zod_1.z.enum(VAT_CATEGORY_VALUES, 'Categorie TVA invalidă.'),
});
/**
 * `endDate`, when present, must fall strictly after `startDate` — plain-data
 * check (no zod dependency) so it can be reused identically by the create
 * schema's `superRefine` (both fields always present) and by
 * SubscriptionsService.update()'s authoritative re-check against the row
 * merged with its persisted state (same pattern as
 * client.schema.ts's `checkClientBusinessRules`).
 */
function checkSubscriptionDates(data) {
    return !data.endDate || data.endDate > data.startDate;
}
// Bare (undefaulted) field definitions, shared by create and update — same
// reasoning as series.schema.ts's `seriesFields`/invoice.schema.ts's
// `invoiceFields`: zod's `.default()` fires on ANY `undefined` input, even a
// field simply absent from a PATCH body, even after `.partial()`. Applying
// `.default()` here and reusing this object for `updateSubscriptionSchema`
// would mean a PATCH `{name: "X"}` alone silently resets currency/autoIssue
// back to their create-time defaults on every update. Defaults are
// therefore only layered on in `createSubscriptionSchema` below.
const subscriptionFields = {
    companyId: zod_1.z.uuid('companyId invalid.'),
    clientId: zod_1.z.uuid('clientId invalid.'),
    seriesId: zod_1.z.uuid('seriesId este obligatoriu și trebuie să fie un UUID valid.'),
    name: zod_1.z.string().trim().min(1, 'Numele este obligatoriu.'),
    frequency: zod_1.z.enum(SUB_FREQUENCY_VALUES, 'Frecvență invalidă.'),
    startDate: isoDateField,
    endDate: isoDateField.optional(),
    issueDay: zod_1.z.coerce.number('Ziua de emitere trebuie să fie un număr.').int().min(1).max(31),
    autoIssue: zod_1.z.boolean(),
    currency: zod_1.z.literal('RON', 'Doar moneda RON este acceptată în această versiune.'),
    paymentTermDays: zod_1.z.coerce.number().int().min(0).optional(),
    notes: zod_1.z.string().trim().optional(),
    items: zod_1.z.array(exports.subscriptionItemInputSchema).min(1, 'Abonamentul trebuie să aibă cel puțin un articol.'),
};
exports.createSubscriptionSchema = zod_1.z
    .object({
    ...subscriptionFields,
    autoIssue: subscriptionFields.autoIssue.default(false),
    currency: subscriptionFields.currency.default('RON'),
})
    .refine((data) => checkSubscriptionDates(data), {
    error: 'Data de sfârșit trebuie să fie ulterioară datei de început.',
    path: ['endDate'],
});
// `companyId` is omitted (a subscription cannot change owner, same as a
// draft invoice or a product). Built from the undefaulted `subscriptionFields`
// (see comment above) so an omitted field stays truly absent instead of
// reappearing as its create-time default. The date-order refine here is a
// fast pre-check only (both fields must be present in THIS payload to
// evaluate it) — SubscriptionsService.update() re-checks against the
// persisted row for a payload that only touches one of the two dates.
exports.updateSubscriptionSchema = zod_1.z
    .object(subscriptionFields)
    .omit({ companyId: true })
    .partial()
    .refine((data) => data.startDate === undefined || data.endDate === undefined || checkSubscriptionDates({ startDate: data.startDate, endDate: data.endDate }), {
    error: 'Data de sfârșit trebuie să fie ulterioară datei de început.',
    path: ['endDate'],
});
exports.listSubscriptionsQuerySchema = common_schema_1.paginationQuerySchema.extend({
    companyId: zod_1.z.uuid('companyId este obligatoriu și trebuie să fie un UUID valid.'),
    status: zod_1.z.enum(SUB_STATUS_VALUES, 'Status invalid.').optional(),
    clientId: zod_1.z.uuid('clientId invalid.').optional(),
});
exports.resumeSubscriptionSchema = zod_1.z.object({
    catchUp: zod_1.z.boolean().default(false),
});
exports.listGenerationLogQuerySchema = common_schema_1.paginationQuerySchema.extend({
    subscriptionId: zod_1.z.uuid('subscriptionId invalid.').optional(),
    status: zod_1.z.enum(GENERATION_LOG_STATUS_VALUES, 'Status invalid.').optional(),
});
